import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { api } from '../services/api';
import { io, Socket } from 'socket.io-client';
import {
  MessageSquare,
  Users,
  Clock,
  CheckCircle,
  Building2,
  Activity,
  Timer,
  BarChart3,
  Zap
} from 'lucide-react';
import { ConversationsTablePanel } from '../components/ConversationsTablePanel';

interface PeriodStats {
  total: number;
  waiting?: number;
  active?: number;
  queued?: number;
  closed: number;
  avaliacao?: number;
}

interface StatsResponse {
  day: PeriodStats;
  week: PeriodStats;
  month: PeriodStats;
  total: { total: number; closed: number; avaliacao?: number };
}

interface SystemInfo {
  environment: string;
  environmentLabel: string;
  whatsappConfigured: boolean;
  whatsappStatus: string;
  whatsappProvider: string | null;
}

export const Dashboard: React.FC = () => {
  const { agent, isAdmin } = useAuth();
  const { systemName } = useSettings();
  const navigate = useNavigate();
  const [statsData, setStatsData] = useState<StatsResponse | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const statsUpdateTimeoutRef = useRef<number | null>(null);

  // Filter states
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [agents, setAgents] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [statsTab, setStatsTab] = useState<'today' | 'history'>('today');

  useEffect(() => {
    loadStats();
    loadFilters();
    if (isLoading) {
      loadSystemInfo();
    }
  }, []);

  // Reload stats when filters change
  useEffect(() => {
    loadStats();
  }, [selectedAgentId, selectedDepartmentId]);

  // WebSocket listener for real-time stats updates
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    console.log('🔌 [Dashboard] Initializing WebSocket for stats updates');

    const socket = io(apiUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ [Dashboard] WebSocket connected:', socket.id);
    });

    socket.on('conversation-updated', (data: any) => {
      console.log('📊 [Dashboard] Conversation updated, scheduling stats refresh...', data);

      // Debounce stats updates to avoid multiple rapid refreshes
      if (statsUpdateTimeoutRef.current) {
        clearTimeout(statsUpdateTimeoutRef.current);
      }

      statsUpdateTimeoutRef.current = setTimeout(() => {
        console.log('📊 [Dashboard] Executing stats refresh');
        loadStats();
      }, 1000); // Wait 1 second before refreshing
    });

    socket.on('disconnect', () => {
      console.log('❌ [Dashboard] WebSocket disconnected');
    });

    socket.on('error', (error: any) => {
      console.error('⚠️ [Dashboard] WebSocket error:', error);
    });

    return () => {
      console.log('🔌 [Dashboard] Cleaning up WebSocket');
      if (statsUpdateTimeoutRef.current) {
        clearTimeout(statsUpdateTimeoutRef.current);
      }
      socket.disconnect();
    };
  }, []);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const params: any = {};
      if (selectedAgentId) params.agentId = selectedAgentId;
      if (selectedDepartmentId) params.departmentId = selectedDepartmentId;

      const stats = await api.getStats(params);
      setStatsData(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingStats(false);
    }
  };

  const loadFilters = async () => {
    try {
      // Load agents based on role
      if (agent?.role === 'DEPARTMENT_MANAGER' && agent.department?.id) {
        // Load agents from the same department
        const allAgents = await api.getAgents();
        const departmentAgents = allAgents.filter((a: any) => a.department?.id === agent.department?.id);
        setAgents(departmentAgents);
      } else if (agent?.role === 'GENERAL_MANAGER' || agent?.role === 'ADMIN') {
        // Load all agents
        const allAgents = await api.getAgents();
        setAgents(allAgents);
      }

      // Load departments for GENERAL_MANAGER and ADMIN
      if (agent?.role === 'GENERAL_MANAGER' || agent?.role === 'ADMIN') {
        const allDepartments = await api.getDepartments();
        setDepartments(allDepartments);
      }
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };

  const loadSystemInfo = async () => {
    try {
      const info = await api.getSystemInfo();
      setSystemInfo(info);
    } catch (error) {
      console.error('Failed to load system info:', error);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-2 border-transparent transition-all duration-200 ${
        onClick
          ? 'cursor-pointer hover:shadow-lg hover:border-blue-400 hover:-translate-y-0.5'
          : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        </div>
        <div className={`p-2 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Conversations Table Panel - Admin Only */}
      {isAdmin() && <ConversationsTablePanel />}

      {/* Statistics Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-t-4 border-blue-500">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Estatísticas de Atendimentos</h2>
            </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            {/* Department Filter (GENERAL_MANAGER and ADMIN only) */}
            {(agent?.role === 'GENERAL_MANAGER' || agent?.role === 'ADMIN') && departments.length > 0 && (
              <select
                value={selectedDepartmentId}
                onChange={(e) => {
                  setSelectedDepartmentId(e.target.value);
                  setSelectedAgentId(''); // Reset agent filter when department changes
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              >
                <option value="">Todos os Departamentos</option>
                {departments.map((dept: any) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            )}

            {/* Agent Filter (DEPARTMENT_MANAGER, GENERAL_MANAGER, and ADMIN) */}
            {(agent?.role === 'DEPARTMENT_MANAGER' || agent?.role === 'GENERAL_MANAGER' || agent?.role === 'ADMIN') && agents.length > 0 && (
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              >
                <option value="">Todos os Atendentes</option>
                {agents
                  .filter((a: any) => {
                    // If department filter is active, only show agents from that department
                    if (selectedDepartmentId) {
                      return a.department?.id === selectedDepartmentId;
                    }
                    return true;
                  })
                  .map((ag: any) => (
                    <option key={ag.id} value={ag.id}>
                      {ag.name} {ag.department ? `(${ag.department.name})` : ''}
                    </option>
                  ))}
              </select>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setStatsTab('today')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${
                statsTab === 'today'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Hoje
              {statsTab === 'today' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
              )}
            </button>
            <button
              onClick={() => setStatsTab('history')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${
                statsTab === 'history'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Histórico
              {statsTab === 'history' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
              )}
            </button>
          </div>
        </div>

        {isLoadingStats || !statsData ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Today Stats */}
            {statsTab === 'today' && (
              <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <StatCard
                  title="Total"
                  value={statsData.day.total}
                  icon={<MessageSquare className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                  onClick={() => navigate('/conversations')}
                />
                <StatCard
                  title="Aguardando"
                  value={statsData.day.waiting || 0}
                  icon={<Timer className="w-6 h-6 text-white" />}
                  color="bg-orange-500"
                  onClick={() => navigate('/conversations?status=WAITING')}
                />
                <StatCard
                  title="Na Fila"
                  value={statsData.day.queued || 0}
                  icon={<Clock className="w-6 h-6 text-white" />}
                  color="bg-yellow-500"
                  onClick={() => navigate('/conversations?status=QUEUED')}
                />
                <StatCard
                  title="Ativos"
                  value={statsData.day.active || 0}
                  icon={<Activity className="w-6 h-6 text-white" />}
                  color="bg-green-500"
                  onClick={() => navigate('/conversations?status=ACTIVE')}
                />
                <StatCard
                  title="Fechados"
                  value={statsData.day.closed}
                  icon={<CheckCircle className="w-6 h-6 text-white" />}
                  color="bg-gray-500"
                  onClick={() => navigate('/conversations?status=CLOSED')}
                />
                {(agent?.role === 'ADMIN' || agent?.role === 'GENERAL_MANAGER') && (
                  <StatCard
                    title="Avaliações"
                    value={statsData.day.avaliacao || 0}
                    icon={<MessageSquare className="w-6 h-6 text-white" />}
                    color="bg-purple-500"
                    onClick={() => navigate('/conversations?status=AVALIACAO')}
                  />
                )}
              </div>
            </div>
            )}

            {/* History Stats */}
            {statsTab === 'history' && (
              <div className="space-y-6">
                {/* Por Semana */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Últimos 7 Dias</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  title="Total"
                  value={statsData.week.total}
                  icon={<MessageSquare className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <StatCard
                  title="Fechados"
                  value={statsData.week.closed}
                  icon={<CheckCircle className="w-6 h-6 text-white" />}
                  color="bg-gray-500"
                />
                {(agent?.role === 'ADMIN' || agent?.role === 'GENERAL_MANAGER') && (
                  <StatCard
                    title="Avaliações"
                    value={statsData.week.avaliacao || 0}
                    icon={<MessageSquare className="w-6 h-6 text-white" />}
                    color="bg-purple-500"
                  />
                )}
              </div>
            </div>

            {/* Por Mês */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Últimos 30 Dias</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  title="Total"
                  value={statsData.month.total}
                  icon={<MessageSquare className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <StatCard
                  title="Fechados"
                  value={statsData.month.closed}
                  icon={<CheckCircle className="w-6 h-6 text-white" />}
                  color="bg-gray-500"
                />
                {(agent?.role === 'ADMIN' || agent?.role === 'GENERAL_MANAGER') && (
                  <StatCard
                    title="Avaliações"
                    value={statsData.month.avaliacao || 0}
                    icon={<MessageSquare className="w-6 h-6 text-white" />}
                    color="bg-purple-500"
                  />
                )}
              </div>
            </div>

            {/* Total desde o início */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Total (desde o início)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  title="Total"
                  value={statsData.total.total}
                  icon={<MessageSquare className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <StatCard
                  title="Fechados"
                  value={statsData.total.closed}
                  icon={<CheckCircle className="w-6 h-6 text-white" />}
                  color="bg-gray-500"
                />
                {(agent?.role === 'ADMIN' || agent?.role === 'GENERAL_MANAGER') && (
                  <StatCard
                    title="Avaliações"
                    value={statsData.total.avaliacao || 0}
                    icon={<MessageSquare className="w-6 h-6 text-white" />}
                    color="bg-purple-500"
                  />
                )}
              </div>
            </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-t-4 border-green-500">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Zap className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ações Rápidas</h2>
          </div>
        <div className={`grid grid-cols-1 ${isAdmin() ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-4`}>
          <button
            onClick={() => navigate('/conversations')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-transparent hover:shadow-lg hover:border-blue-400 hover:-translate-y-0.5 transition-all duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Ver Atendimentos</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar atendimentos</p>
              </div>
            </div>
          </button>

          {isAdmin() && (
            <button
              onClick={() => navigate('/departments')}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-transparent hover:shadow-lg hover:border-purple-400 hover:-translate-y-0.5 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Departamentos</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar setores</p>
                </div>
              </div>
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className={`grid grid-cols-1 ${isAdmin() ? 'md:grid-cols-2' : ''} gap-6`}>
        {/* System Info - Only for Admin */}
        {isAdmin() && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-t-4 border-purple-500 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Informações do Sistema
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Status do Sistema:</span>
                <span className="font-medium text-green-600 dark:text-green-400">✓ Online</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Modo de Operação:</span>
                <span className={`font-medium ${
                  systemInfo?.environment === 'production'
                    ? 'text-green-600 dark:text-green-400'
                    : systemInfo?.environment === 'staging'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {systemInfo?.environmentLabel || 'Carregando...'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600 dark:text-gray-400">Integração WhatsApp:</span>
                <div className="text-right">
                  <span className={`font-medium ${
                    systemInfo?.whatsappConfigured
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {systemInfo?.whatsappStatus || 'Carregando...'}
                  </span>
                  {systemInfo?.whatsappProvider && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {systemInfo.whatsappProvider}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-t-4 border-indigo-500 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Informações do Usuário
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Nome:</span>
              <span className="font-medium text-gray-900 dark:text-white">{agent?.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Email:</span>
              <span className="font-medium text-gray-900 dark:text-white">{agent?.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Função:</span>
              <span className="font-medium text-blue-600 dark:text-blue-400">{agent?.role}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600 dark:text-gray-400">Departamento:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {agent?.department?.name || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      {statsData && statsData.total.total === 0 && isAdmin() && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            Bem-vindo ao {systemName}
          </h3>
          <p className="text-gray-400 mb-4">
            Este é um sistema de atendimento ao cliente integrado com WhatsApp.
            {!systemInfo?.whatsappConfigured && (
              <span> Como você está em modo de {systemInfo?.environmentLabel?.toLowerCase() || 'desenvolvimento'} sem integração WhatsApp ativa, não há atendimentos no momento.</span>
            )}
            {systemInfo?.whatsappConfigured && (
              <span> A integração com WhatsApp está ativa via {systemInfo?.whatsappProvider}.</span>
            )}
          </p>
          {!systemInfo?.whatsappConfigured && (
            <div className="bg-gray-900 border border-gray-700 rounded p-4 text-sm text-gray-300">
              <p className="font-medium mb-2 text-white">Para começar:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Configure as credenciais do WhatsApp Business API no arquivo .env do backend</li>
                <li>Os atendimentos aparecerão automaticamente quando clientes enviarem mensagens</li>
                <li>Você pode gerenciar departamentos na seção "Departments"</li>
                <li>Explore a interface e se familiarize com as funcionalidades</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
